import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ShieldCheck } from '@phosphor-icons/react';
import { PD_POLICY_VERSION, type ProfileResponse } from '@app/shared';
import { useT } from '../i18n';
import { api, ApiError } from '../lib/api';
import { haptic, openExternalLink } from '../lib/tma';
import { Logo } from './shell/Logo';
import { Button, Card, Toast } from './ui';

/** One tap accepts the privacy notice of the current version. */
export function ConsentScreen() {
  const t = useT();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  async function submit() {
    setBusy(true);
    setFailed(false);
    try {
      const profile = await api.acceptConsent({ policyVersion: PD_POLICY_VERSION });
      haptic('success');
      queryClient.setQueryData<ProfileResponse>(['profile'], profile);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'POLICY_OUTDATED') {
        // This bundle carries an old notice version: reload to get the new text.
        window.location.reload();
        return;
      }
      setFailed(true);
      haptic('error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen p-6 max-w-lg mx-auto flex flex-col items-center justify-center text-center safe-top safe-bottom">
      <Logo className="mb-6" />
      <Card variant="surface" padding="none" className="w-full p-5">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-accent-soft text-accent-soft-on mb-4">
          <ShieldCheck size={28} />
        </div>
        <h1 className="text-xl font-extrabold mb-2">{t.consent.title}</h1>
        <p className="text-sm text-hint mb-4">{t.consent.text}</p>
        <div className="flex flex-col items-center gap-2 mb-6">
          <button
            type="button"
            className="text-sm font-semibold text-fg underline"
            onClick={() => openExternalLink(`${window.location.origin}/privacy.html`)}
          >
            {t.consent.link}
          </button>
          <button
            type="button"
            className="text-sm font-semibold text-fg underline"
            onClick={() => openExternalLink(`${window.location.origin}/offer.html`)}
          >
            {t.consent.offerLink}
          </button>
        </div>
        <Button variant="primary" size="lg" fullWidth loading={busy} onClick={submit}>
          {t.consent.accept}
        </Button>
        {failed && (
          <Toast tone="destructive" className="mt-3">
            {t.common.failed}
          </Toast>
        )}
      </Card>
    </div>
  );
}
